import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { findWikiCategory, indexWikiCategories, wikiKeys } from './wikiData';
import { parseWikiPath } from './wikiRoutes';

/**
 * The TopBar's view of the Wiki (spec A §1.4), computed from the route and
 * whatever the Wiki has already loaded into the React Query cache — the crumb
 * never fetches on its own.
 *
 *   label     "Home" | "Ask the Wiki" | "To acknowledge" | "AI Brain" |
 *             "<Category>" | "<Category> / <Subcategory>" | "<page title>"
 *             ('' while the category/page it needs is not cached yet)
 *   location  the New-page modal's default location for this route:
 *             { categoryId, subcategoryId } from the open category,
 *             subcategory or page; {} when there is none (the modal then
 *             picks the first available location).
 */

const NOT_WIKI = Object.freeze({ isWiki: false, route: null, label: '', location: null });

const STATIC_LABELS = Object.freeze({
    home: 'Home',
    ask: 'Ask the Wiki',
    ack: 'To acknowledge',
    brain: 'AI Brain',
    archived: 'Archived',
});

function pageTitle(page) {
    const doc = page?.page || page;
    return doc?.title || doc?.name || '';
}

/** Pure label resolver, exported for tests. */
export function wikiCrumbLabel(route, { categories, page } = {}) {
    if (!route) return '';
    if (STATIC_LABELS[route.view]) return STATIC_LABELS[route.view];
    if (route.view === 'category') {
        const found = findWikiCategory(indexWikiCategories(categories), route.id);
        if (!found) return '';
        return found.isSub ? `${found.parent.name} / ${found.category.name}` : (found.category.name || '');
    }
    if (route.view === 'page') return pageTitle(page);
    return 'Home';
}

/** Pure location resolver, exported for tests. */
export function wikiRouteLocation(route, { categories, page } = {}) {
    if (!route) return {};
    if (route.view === 'category') {
        const found = findWikiCategory(indexWikiCategories(categories), route.id);
        if (!found) return {};
        return found.isSub
            ? { categoryId: found.parent.id, subcategoryId: found.category.id }
            : { categoryId: found.category.id, subcategoryId: null };
    }
    if (route.view === 'page') {
        const doc = page?.page || page;
        if (!doc?.category_id) return {};
        return { categoryId: doc.category_id, subcategoryId: doc.subcategory_id || null };
    }
    return {};
}

const noopUnsubscribe = () => {};

/**
 * Read one cache entry and re-render when it changes. Works without a
 * QueryClientProvider (returns undefined) so shell surfaces never crash.
 */
function useCachedQueryData(queryClient, key) {
    const keyHash = key ? JSON.stringify(key) : '';
    const subscribe = useCallback(
        (listener) => (queryClient ? queryClient.getQueryCache().subscribe(listener) : noopUnsubscribe),
        [queryClient],
    );
    const read = () => (queryClient && keyHash ? queryClient.getQueryData(JSON.parse(keyHash)) : undefined);
    return useSyncExternalStore(subscribe, read, read);
}

export default function useWikiCrumb(pathname) {
    const queryClient = useContext(QueryClientContext);
    const route = useMemo(() => parseWikiPath(pathname), [pathname]);
    const categories = useCachedQueryData(queryClient, route ? wikiKeys.categories() : null);
    const page = useCachedQueryData(queryClient, route?.view === 'page' ? wikiKeys.page(route.id) : null);
    return useMemo(() => {
        if (!route) return NOT_WIKI;
        return {
            isWiki: true,
            route,
            label: wikiCrumbLabel(route, { categories, page }),
            location: wikiRouteLocation(route, { categories, page }),
        };
    }, [route, categories, page]);
}
